import React, { useState } from 'react';
import { useResolution } from '../../contexts/ResolutionContext';
import CountResolutionForm from './Forms/CountResolutionForm';
import BinaryResolutionForm from './Forms/BinaryResolutionForm';
import MetricResolutionForm from './Forms/MetricResolutionForm';
import StreakResolutionForm from './Forms/StreakResolutionForm';
import { ResolutionTypes, ResolutionTypeOptions } from '../../constants/resolutionTypes';
import { useLanguage } from '../../contexts/LanguageContext';

const AddResolutionModal = ({ isOpen, onClose }) => {
	const { addResolution, getSmartResolutionYear } = useResolution();

	const [resolutionText, setResolutionText] = useState('');
	const [resolutionCount, setResolutionCount] = useState('');
	const [startValue, setStartValue] = useState('');
	const [targetValue, setTargetValue] = useState('');
	const [unit, setUnit] = useState('');
	const [resolutionType, setResolutionType] = useState(ResolutionTypes.COUNT);
	const [encrypt, setEncrypt] = useState(false);
	const [encryptionKey, setEncryptionKey] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [resolutionPeriod, setResolutionPeriod] = useState('daily');

	// Get the smart resolution year (current year or next year based on the month)
	const resolutionYear = getSmartResolutionYear();
	const { t } = useLanguage();

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsSubmitting(true);
		const resolution = {
			text: resolutionText,
			type: resolutionType,
			period: resolutionType === ResolutionTypes.STREAK ? resolutionPeriod : undefined,
			count: resolutionCount ? parseInt(resolutionCount) : null,
			startValue: startValue !== '' ? parseFloat(startValue) : null,
			targetValue: targetValue !== '' ? parseFloat(targetValue) : null,
			unit: unit || null,
			year: resolutionYear,
			encrypt,
			encryptionKey: encrypt ? encryptionKey : null,
			completedCount: 0,
			// streak specific defaults
			currentStreak: resolutionType === ResolutionTypes.STREAK ? 0 : undefined,
			longestStreak: resolutionType === ResolutionTypes.STREAK ? 0 : undefined,
			lastLogDate: resolutionType === ResolutionTypes.STREAK ? null : undefined,
			measurements:
				startValue !== ''
					? [
							{
								date: new Date().toISOString(),
								value: parseFloat(startValue),
								note: 'baseline',
							},
					  ]
					: [],
			expireDate: new Date(resolutionYear, 11, 31, 23, 59, 59).toISOString(),
		};
		const success = await addResolution(resolution, t);
		setIsSubmitting(false);
		if (success) {
			handleClose();
		}
	};

	const handleClose = () => {
		setResolutionText('');
		setResolutionCount('');
		setStartValue('');
		setTargetValue('');
		setUnit('');
		setEncrypt(false);
		setEncryptionKey('');
		setIsSubmitting(false);
		onClose();
	};

	if (!isOpen) return null;

	if (!isOpen) return null;

	const getExampleText = () => {
		switch (resolutionType) {
			case ResolutionTypes.COUNT:
				return t('exampleCount');
			case ResolutionTypes.BINARY:
				return t('exampleBinary');
			case ResolutionTypes.METRIC:
				return t('exampleMetric');
			case ResolutionTypes.STREAK:
				return t('exampleStreak');
			default:
				return '';
		}
	};

	return (
		<div className='fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-50'>
			<div className='bg-white rounded-lg shadow-lg w-full max-w-lg mx-4'>
				<div className='p-6'>
					<h2 className='text-xl font-bold mb-4'>{t('addResolution')}</h2>
					<form onSubmit={handleSubmit}>
						<div className='mb-4'>
							<label className='block text-sm font-medium text-gray-700 mb-1'>{t('type')}</label>
							<select
								value={resolutionType}
								onChange={(e) => setResolutionType(e.target.value)}
								className='w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm focus:ring-blue-500 focus:border-blue-500'
							>
								{ResolutionTypeOptions.map((opt) => (
									<option key={opt} value={opt}>
										{t(opt)}
									</option>
								))}
							</select>
							<p className='text-sm text-gray-500 mt-1'>{getExampleText()}</p>
						</div>

						{resolutionType === ResolutionTypes.COUNT && (
							<CountResolutionForm
								resolutionText={resolutionText}
								setResolutionText={setResolutionText}
								resolutionCount={resolutionCount}
								setResolutionCount={setResolutionCount}
								resolutionYear={resolutionYear}
							/>
						)}

						{resolutionType === ResolutionTypes.BINARY && (
							<BinaryResolutionForm
								resolutionText={resolutionText}
								setResolutionText={setResolutionText}
								resolutionYear={resolutionYear}
							/>
						)}

						{resolutionType === ResolutionTypes.METRIC && (
							<MetricResolutionForm
								resolutionText={resolutionText}
								setResolutionText={setResolutionText}
								startValue={startValue}
								setStartValue={setStartValue}
								targetValue={targetValue}
								setTargetValue={setTargetValue}
								unit={unit}
								setUnit={setUnit}
								resolutionYear={resolutionYear}
							/>
						)}

						{resolutionType === ResolutionTypes.STREAK && (
							<StreakResolutionForm
								resolutionText={resolutionText}
								setResolutionText={setResolutionText}
								resolutionYear={resolutionYear}
								period={resolutionPeriod}
								setPeriod={setResolutionPeriod}
							/>
						)}

						<div className='mb-4 flex items-center'>
							<input
								type='checkbox'
								id='encrypt'
								checked={encrypt}
								onChange={(e) => setEncrypt(e.target.checked)}
								className='mr-2'
							/>
							<label htmlFor='encrypt' className='text-sm font-medium text-gray-700'>
								{t('makePrivate')}
							</label>
						</div>

						{encrypt && (
							<div className='mb-4'>
								<label className='block text-sm font-medium text-gray-700 mb-1'>
									{t('encryptionKey')}
								</label>
								<input
									type='password'
									value={encryptionKey}
									onChange={(e) => setEncryptionKey(e.target.value)}
									className='w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm focus:ring-blue-500 focus:border-blue-500'
									placeholder={t('enterEncryptionKey')}
									required
								/>
								<p className='text-xs text-gray-500 mt-1'>{t('rememberEncryptionKey')}</p>
							</div>
						)}
					</form>
				</div>
				<div className='bg-gray-50 px-6 py-3 rounded-b-lg flex justify-end space-x-3'>
					<button
						type='button'
						onClick={handleClose}
						className='px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-400'
						disabled={isSubmitting}
					>
						{t('cancel')}
					</button>
					<button
						type='submit'
						form=''
						onClick={handleSubmit}
						className='px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed'
						disabled={isSubmitting || !resolutionText.trim()}
					>
						{isSubmitting ? (
							<>
								<span className='inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></span>
								Adding...
							</>
						) : (
							t('addResolution')
						)}
					</button>
				</div>
			</div>
		</div>
	);
};

export default AddResolutionModal;
